'use client';

import { getSiteConfig } from '@shared/config';
import { stringifyArray } from '@shared/utils';
import { useTranslations } from 'next-intl';
import type { EditableNavigationMenuItemProps } from '../../../NavigationMenu/components/NavigationMenuItem/types';
import { NavigationMenu } from '../../../NavigationMenu/NavigationMenu';
import { mapApiKeysToChannelLabels } from '../utils/channelsMapper';

type ContactPreferencesSectionProps = {
  namespace: string;
  className?: string;
  productPreference: string[];
  channels: string[];
  onEditProductPreferences: () => void;
  onEditChannels: () => void;
};

function ContactPreferencesSection({ namespace, className, productPreference, channels, onEditProductPreferences, onEditChannels }: ContactPreferencesSectionProps) {
  const t = useTranslations(namespace);
  const { showsProductPreference } = getSiteConfig().MY_ACCOUNT_SETTINGS;

  const contactPreferencesItems: EditableNavigationMenuItemProps[] = [];

  if (showsProductPreference) {
    contactPreferencesItems.push({
      label: t('contact_preferences.product_preference'),
      data: productPreference.length ? stringifyArray(productPreference) : 'Not set',
      buttonText: t('edit'),
      chevron: false,
      onClick: onEditProductPreferences,
      variant: 'editable',
      'data-testid': 'edit-product-preferences',
      'data-testid-value': 'product-preferences-summary',
      'data-track-name': 'Products',
      'data-track-container': 'Products',
      'button-data-testid': 'edit-product-preferences-button',
    });
  }

  contactPreferencesItems.push({
    label: t('contact_preferences.channels'),
    data: channels.length ? stringifyArray(mapApiKeysToChannelLabels(channels, t)) : 'Not set',
    buttonText: t('edit'),
    chevron: false,
    onClick: onEditChannels,
    variant: 'editable',
    'data-testid': 'edit-channels',
    'data-testid-value': 'channels-summary',
    'data-track-name': 'Channels',
    'data-track-container': 'Channels',
    'button-data-testid': 'edit-channels-button',
  });

  return (
    <div className="flex flex-col gap-4" data-track-section="Contact Preferences" data-testid="contact-preferences-section">
      <div className="flex flex-col gap-0.5">
        <p className="paragraph-strong">{t('contact_preferences.title')}</p>
        <p className="paragraph-s">{t('contact_preferences.description')}</p>
      </div>
      <NavigationMenu variant="secondary" items={contactPreferencesItems} className={className} />
    </div>
  );
}

export { ContactPreferencesSection };
